import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/Header';
import { BottomNav } from '../components/BottomNav';
import { ProductGrid, PRODUCTOS } from '../components/ProductGrid';
import { SignaturePad } from '../components/SignaturePad';
import { useAuth } from '../contexts/AuthContext';
import { useOffline } from '../contexts/OfflineContext';
import { Truck, Check, AlertCircle, ArrowRight, MapPin, Package } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function Transferencias() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [receivedQuantities, setReceivedQuantities] = useState({});
  const [showSignature, setShowSignature] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadTransfers();
  }, []);

  const loadTransfers = async () => {
    try {
      const { data } = await axios.get(`${API}/transferencias/pendientes`);
      setPendingTransfers(data);
    } catch (error) {
      console.error('Error loading transfers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTransfer = (transfer) => {
    setSelectedTransfer(transfer);
    // Pre-fill with expected quantities
    setReceivedQuantities(transfer.productos || {});
  };

  const handleValidate = () => {
    if (Object.values(receivedQuantities).every(v => !v || v === 0)) {
      setError('Ingresa las cantidades recibidas');
      return;
    }
    setError('');
    setShowSignature(true);
  };

  const handleSignatureSave = async (signatureData) => {
    setShowSignature(false);
    setSubmitting(true);
    
    try {
      await axios.put(`${API}/transferencias/${selectedTransfer.id}/validar`, {
        productos_recibidos: receivedQuantities,
        firma_destino: signatureData,
      });
      setSelectedTransfer(null);
      setReceivedQuantities({});
      await loadTransfers();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al validar transferencia');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  // View for validating a transfer
  if (selectedTransfer) {
    return (
      <div className="min-h-screen bg-zinc-50" data-testid="validate-transfer-page">
        <Header 
          title="Validar Recepción" 
          showBack 
          onBack={() => {
            setSelectedTransfer(null);
            setReceivedQuantities({});
          }} 
        />
        
        <div className="page-container fade-in">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-red-600" />
              <p className="text-red-700 font-medium">{error}</p>
            </div>
          )}

          {/* Transfer Info */}
          <div className="summary-card mb-6">
            <div className="flex items-center gap-3 mb-4">
              <Truck className="w-8 h-8 text-blue-600" />
              <div>
                <p className="font-bold text-lg">{selectedTransfer.repartidor_name}</p>
                <p className="text-zinc-600">{selectedTransfer.punto_origen} → {selectedTransfer.punto_destino}</p>
              </div>
            </div>
            
            <h4 className="font-bold mb-2">Cantidad enviada:</h4>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(selectedTransfer.productos || {}).map(([clave, cantidad]) => {
                const producto = PRODUCTOS.find(p => p.clave === clave);
                return (
                  <div key={clave} className="text-center p-2 bg-zinc-100 rounded-lg">
                    <span className="text-2xl">{producto?.icon}</span>
                    <p className="font-bold">{cantidad}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Received Quantities */}
          <h3 className="text-xl font-bold text-zinc-900 mb-4">
            Cantidad Recibida
          </h3>
          <p className="text-zinc-600 mb-4">
            Verifica y ajusta las cantidades que recibiste
          </p>
          
          <ProductGrid
            quantities={receivedQuantities}
            onQuantityChange={(clave, qty) => setReceivedQuantities({ ...receivedQuantities, [clave]: qty })}
          />

          <button
            onClick={handleValidate}
            disabled={submitting}
            className="w-full h-20 bg-green-600 text-white rounded-2xl text-2xl font-bold btn-press flex items-center justify-center gap-3 mt-6 disabled:opacity-50"
            data-testid="btn-validate-transfer"
          >
            {submitting ? (
              <div className="spinner w-8 h-8 border-white border-t-transparent" />
            ) : (
              <>
                <Check className="w-8 h-8" />
                Firmar y Confirmar
              </>
            )}
          </button>
        </div>
        
        <BottomNav />

        {showSignature && (
          <SignaturePad
            title="Firma de Recepción"
            onSave={handleSignatureSave}
            onCancel={() => setShowSignature(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="transferencias-page">
      <Header title="Transferencias" />
      
      <div className="page-container fade-in">
        {/* Repartidor: Create new transfer */}
        {user?.role === 'repartidor' && (
          <button
            onClick={() => navigate('/transferencias/nueva')}
            className="w-full h-20 bg-blue-600 text-white rounded-2xl text-2xl font-bold btn-press flex items-center justify-center gap-3 mb-6"
            data-testid="btn-nueva-transferencia"
          >
            <Truck className="w-8 h-8" />
            Nueva Entrega
          </button>
        )}

        {/* Pending Transfers */}
        <h3 className="text-xl font-bold text-zinc-900 mb-4 flex items-center gap-2">
          <Package className="w-6 h-6" />
          Entregas Pendientes ({pendingTransfers.length})
        </h3>

        {pendingTransfers.length === 0 ? (
          <div className="p-6 bg-zinc-100 rounded-xl text-center">
            <p className="text-zinc-600">No hay entregas pendientes</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pendingTransfers.map((transfer) => (
              <button
                key={transfer.id}
                onClick={() => handleSelectTransfer(transfer)}
                className="w-full bg-white border-2 border-zinc-200 rounded-xl p-4 text-left btn-press"
                data-testid={`transfer-${transfer.id}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <Truck className="w-6 h-6 text-blue-600" />
                    <span className="font-bold">{transfer.repartidor_name}</span>
                  </div>
                  <ArrowRight className="w-5 h-5 text-zinc-400" />
                </div>
                
                <div className="flex items-center gap-2 text-zinc-600 mb-2">
                  <MapPin className="w-4 h-4" />
                  <span>{transfer.punto_origen} → {transfer.punto_destino}</span>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {Object.entries(transfer.productos || {}).map(([clave, cantidad]) => {
                    const producto = PRODUCTOS.find(p => p.clave === clave);
                    return (
                      <span key={clave} className="px-3 py-1 bg-zinc-100 rounded-full text-sm font-medium">
                        {producto?.nombre}: {cantidad}
                      </span>
                    );
                  })}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      
      <BottomNav />
    </div>
  );
}
